import {
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    Icon,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    SaleActivityDto,
    SaleActivityStatus,
    SaleActivityType,
} from "../../../api/psc";
import { ConfirmationDialog } from "../../../components/exports";
import CustomizedCheckbox from "../../../components/shared/CustomizedCheckbox";
import { SaleActivityTypeConstants } from "../../../constants/SaleConstants";
import { DialogContext } from "../../../contexts/DialogContext";
import { UserContext } from "../../../contexts/UserContext";
import { deleteActivity, updateActivity } from "../../../slices/ActivitySlice";
import { RootState } from "../../../slices/Store";
import { toJalaliDate } from "../../../utils/date";
import ProjectDetailDialog from "../../project-detail/ProjectDetailDialog";
import AddSaleActivityDialog from "../dialogs/AddSaleActivityDialog";

export default function SaleActivityItem(props: {
    saleId: number;
    activity: SaleActivityDto;
    showProjectViewButton?: boolean;
}) {
    const user = useContext(UserContext);
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const activitiesState = useSelector(
        (state: RootState) => state.activityState
    );

    const [requestInProgress, setRequestInProgress] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { openDialog, closeDialog } = useContext(DialogContext);

    const isCreatorOrOwner =
        props.activity.userId === user.id || user.role === 1;
    let isAssigned = true;

    const typeConstant = SaleActivityTypeConstants.find(
        (t) => t.id === props.activity.type
    );
    const isChecked = props.activity.status === SaleActivityStatus.DONE;

    const handleStatusChange = async (checked: boolean) => {
        setRequestInProgress(true);

        await updateActivity(
            props.activity.id,
            {
                ...props.activity,
                status: checked
                    ? SaleActivityStatus.DONE
                    : SaleActivityStatus.PENDING,
            },
            activitiesState,
            dispatch
        );

        setRequestInProgress(false);
    };

    const handleDeleteActivity = () => {
        openDialog(
            <ConfirmationDialog
                title="مایل به حذف این فعالیت هستید؟"
                onAccept={() => {
                    deleteActivity(
                        props.activity.id,
                        activitiesState,
                        dispatch
                    ).then((r) => {});
                }}
            />
        );
    };

    const handleEditActivity = () => {
        openDialog(
            <AddSaleActivityDialog
                saleId={props.saleId}
                activity={props.activity}
            />
        );
    };

    const openMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const openProject = () => {
        openDialog(
            <ProjectDetailDialog
                projectHashId={props.activity.projectHashId}
                openSales={true}
            />,
            () => navigate(`#`)
        );
    };

    const header = (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
                src={props.activity.user.avatarUrl}
                sx={{ width: 35, height: 35 }}
            />

            <Stack flexGrow={1}>
                <Typography color="primary" sx={{ flexGrow: 1 }}>
                    {props.activity.user.displayName}
                </Typography>
                <Typography fontSize="smaller" color="text.secondary">
                    {toJalaliDate(props.activity.createDate)}
                </Typography>
            </Stack>

            <Chip
                sx={{ bgcolor: typeConstant.color, color: "white" }}
                label={typeConstant.title}
                size="small"
            />

            <IconButton onClick={openMenu}>
                <Icon>more_vert</Icon>
            </IconButton>

            <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
                <MenuItem
                    onClick={handleEditActivity}
                    disabled={!isCreatorOrOwner}
                >
                    ویرایش
                </MenuItem>
                <Divider />
                <MenuItem
                    onClick={handleDeleteActivity}
                    disabled={!isCreatorOrOwner}
                >
                    حذف
                </MenuItem>
            </Menu>
        </Box>
    );

    switch (props.activity.type) {
        case SaleActivityType.NOTE:
            return (
                <Stack sx={{ p: 1.5 }} spacing={1}>
                    {header}
                    <Typography>{props.activity.requestNotes}</Typography>
                </Stack>
            );
        case SaleActivityType.REMINDER:
            isAssigned = props.activity.userId === user.id;
            return (
                <Stack sx={{ p: 1.5 }} spacing={1}>
                    {header}
                    <Stack direction="row" alignItems="center" flexWrap="wrap">
                        <CustomizedCheckbox
                            onChange={handleStatusChange}
                            checked={isChecked}
                            disabled={requestInProgress || !isAssigned}
                        />
                        <Typography>{props.activity.requestNotes}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography color="text.secondary" fontSize="smaller">
                            یادآوری برای تاریخ
                        </Typography>
                        <Typography color="primary" sx={{ flexGrow: 1 }}>
                            {toJalaliDate(props.activity.dueDate)}
                        </Typography>
                    </Stack>
                    {props.showProjectViewButton && (
                        <Button
                            color="secondary"
                            variant="outlined"
                            onClick={openProject}
                            startIcon={<Icon>visibility</Icon>}
                        >
                            مشاهده فرایند فروش
                        </Button>
                    )}
                </Stack>
            );
        case SaleActivityType.TASK:
            isAssigned = props.activity.assignedUserId === user.id;
            return (
                <Stack sx={{ p: 1.5 }} spacing={1}>
                    {header}
                    <Stack direction="row" flexWrap="wrap" alignItems="center">
                        <CustomizedCheckbox
                            onChange={handleStatusChange}
                            checked={isChecked}
                            disabled={requestInProgress || !isAssigned}
                        />
                        <Typography>{props.activity.requestNotes}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography color="text.secondary" fontSize="smaller">
                            اختصاص داده شده به
                        </Typography>
                        <Typography color="primary" sx={{ flexGrow: 1 }}>
                            {props.activity.assignedUser.displayName}
                        </Typography>
                    </Stack>
                    {props.showProjectViewButton && (
                        <Button
                            color="secondary"
                            variant="outlined"
                            onClick={openProject}
                            startIcon={<Icon>visibility</Icon>}
                        >
                            مشاهده فرایند فروش
                        </Button>
                    )}
                </Stack>
            );
    }
}
